import { Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import TrustBar from "@/components/TrustBar";
import PaymentLogos from "@/components/PaymentLogos";
import { COMPANY } from "@/pages/legal/LegalLayout";
import { Phone, Mail, MapPin } from "lucide-react";
import {
  ArrowUpRight, Zap, Sparkles, MessageCircle, LineChart,
  ShieldCheck, Lock, Radio, TrendingUp, ArrowRight,
} from "lucide-react";

/* ---------- Animated live-ticker — real Harem Altın rates when available ---------- */
function DemoTicker() {
  const [rates, setRates] = useState(null);
  const [t, setT] = useState(0);
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/public/rates`, { cache: "no-store" });
        if (r.ok) setRates(await r.json());
      } catch {}
    };
    fetchRates();
    const poll = setInterval(fetchRates, 10000);
    const tick = setInterval(() => setT((x) => x + 1), 1800);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);
  const goldGr = Number(rates?.gold?.selling_price ?? 6501.20);
  const silverGr = Number(rates?.silver?.selling_price ?? 94.42);
  const platGr = Number(rates?.platinum?.selling_price ?? 1759.80);
  const goldChg = rates?.gold ? goldGr - Number(rates.gold.buying_price) : Math.sin(t / 3) * 4.2;
  const items = [
    { code: "GRAM ALTIN",  price: goldGr,           delta: goldChg,                              unit: "₺" },
    { code: "ÇEYREK",      price: goldGr * 1.75,     delta: goldChg * 1.75,                        unit: "₺" },
    { code: "CUMHURİYET",  price: goldGr * 7.19,     delta: goldChg * 7.19,                        unit: "₺" },
    { code: "USD/TRY",     price: 41.28,             delta: Math.cos(t / 5) * 0.03,                unit: "₺", frac: 3 },
    { code: "GÜMÜŞ",       price: silverGr,          delta: Math.sin(t / 3.7) * 0.5,               unit: "₺" },
    { code: "PLATİN",      price: platGr,            delta: Math.sin(t / 4.5 + 2) * 2.9,           unit: "₺" },
  ];
  return (
    <div className="flex overflow-hidden border-y border-white/8 bg-[#080808]">
      <div className="flex gap-0 animate-[marquee_40s_linear_infinite]" style={{minWidth:"200%"}}>
        {[...items, ...items].map((it, i) => {
          const isUp = Number(it.delta) >= 0;
          const prefix = it.unit === "$" ? "$" : "₺";
          return (
            <div key={i} className="flex items-center gap-3 px-6 py-3 border-r border-white/8 whitespace-nowrap">
              <span className="text-[10px] font-mono tracking-widest text-neutral-500">{it.code}</span>
              <span className="font-mono text-sm text-white tabular-nums">{prefix}{Number(it.price).toLocaleString("tr-TR", {maximumFractionDigits: it.frac ?? 2})}</span>
              <span className={`text-[10px] font-mono ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                {isUp ? "▲" : "▼"} {Math.abs(Number(it.delta)).toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/* ---------- POS Mockup — live data from /api/public/rates ---------- */
function PosMockup() {
  const [rates, setRates] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchRates = async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/public/rates`, { cache: "no-store" });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setRates(d);
      } catch {}
    };
    fetchRates();
    // Poll every 8s so the mockup feels alive without hammering the API.
    const id = setInterval(() => { fetchRates(); setTick((x) => x + 1); }, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Metal live prices (₺/gr) — fall back to sensible defaults on cold start.
  const goldGr = Number(rates?.gold?.selling_price ?? 6500.83);
  const silverGr = Number(rates?.silver?.selling_price ?? 94.42);
  const platGr = Number(rates?.platinum?.selling_price ?? 1500);

  // Marketing demo catalogue. Prices computed live from rates.
  const catalogue = [
    { n: "22K Bilezik",   w: 15.42, purity: 0.916,  metal: "gold",     c: "var(--gold)" },
    { n: "14K Zincir",    w: 8.21,  purity: 0.585,  metal: "gold",     c: "#c2a24a" },
    { n: "Cumhuriyet",    w: 7.21,  purity: 0.916,  metal: "gold",     c: "var(--gold)" },
    { n: "925 Yüzük",     w: 4.5,   purity: 0.925,  metal: "silver",   c: "#cccccc" },
    { n: "18K Tektaş",    w: 2.15,  purity: 0.750,  metal: "gold",     c: "var(--gold)" },
    { n: "Platin Alyans", w: 6.8,   purity: 0.950,  metal: "platinum", c: "#e5e5e5" },
  ];
  const rateOf = (m) => (m === "gold" ? goldGr : m === "silver" ? silverGr : platGr);
  // Small marketing markup (5%) — mirrors the app's default 3% + typical workmanship.
  const priceOf = (p) => Math.round(p.w * p.purity * rateOf(p.metal) * 1.05);
  const fmt = (n) => Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 });

  // Sample basket (fixed selection, live-priced totals).
  const basket = [
    { ...catalogue[0], q: 1 },
    { ...catalogue[2], q: 2 },
    { ...catalogue[4], q: 1 },
  ];
  const basketTotal = basket.reduce((s, it) => s + priceOf(it) * it.q, 0);

  const goldChange = rates?.gold ? Number(rates.gold.selling_price) - Number(rates.gold.buying_price) : 12.40;
  const goldChangePct = rates?.gold?.buying_price ? (goldChange / rates.gold.buying_price) * 100 : 0.19;

  return (
    <div className="relative" data-testid="pos-mockup">
      {/* Screen bezel */}
      <div className="border border-white/10 bg-[#0a0a0a] shadow-[0_50px_100px_-20px_rgba(212,175,55,0.25)] overflow-hidden">
        {/* Titlebar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/8" style={{background:"#050505"}}>
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></div>
          <div className="text-[10px] font-mono text-neutral-500 ml-3">milyemsoft.com/pos</div>
          <div className="ml-auto flex items-center gap-2">
            <Radio size={10} className="animate-pulse" style={{color:"var(--gold)"}} />
            <div className="text-[10px] font-mono tracking-widest" style={{color:"var(--gold)"}}>CANLI · MİLYEMSOFT</div>
          </div>
        </div>
        {/* Content: left product grid, right cart */}
        <div className="grid grid-cols-5 gap-0">
          <div className="col-span-3 border-r border-white/8 p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Ürünler · Canlı Fiyat</div>
            <div className="grid grid-cols-3 gap-2">
              {catalogue.map((p, i) => (
                <div key={i} className="border border-white/8 p-3 hover:bg-white/[.02] transition-colors" data-testid={`demo-product-${i}`}>
                  <div className="h-8 flex items-center justify-center border border-white/8 mb-2" style={{background:"#0a0a0a"}}>
                    <div className="w-4 h-4 rounded-full opacity-60" style={{background:p.c}}></div>
                  </div>
                  <div className="text-[10px] text-white truncate">{p.n}</div>
                  <div className="text-[9px] text-neutral-500 font-mono">{p.w} gr · {(p.purity*1000).toFixed(0)}</div>
                  <div className="text-[10px] font-mono mt-1" style={{color:"var(--gold)"}}>₺{fmt(priceOf(p))}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-2 p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Sepet</div>
            <div className="space-y-2 text-[10px]">
              {basket.map((it, i) => (
                <div key={i} className="flex justify-between border-b border-white/6 py-1.5">
                  <span className="text-neutral-300">{it.n} ×{it.q}</span>
                  <span className="font-mono" style={{color:"var(--gold)"}}>₺{fmt(priceOf(it) * it.q)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[--gold]/30 flex justify-between items-baseline">
              <span className="text-[10px] tracking-widest uppercase text-neutral-500">Toplam</span>
              <span className="font-display text-lg" style={{color:"var(--gold)"}} data-testid="demo-basket-total">₺{fmt(basketTotal)}</span>
            </div>
            <div className="mt-3 py-2 text-center text-[10px] font-mono" style={{background:"var(--gold)", color:"#0a0a0a"}}>
              TAHSİL ET
            </div>
          </div>
        </div>
      </div>
      {/* Floating rate card — real gram gold */}
      <div className="absolute -bottom-6 -left-4 sm:-left-8 hidden md:block border border-white/10 bg-[#080808] p-4 w-56 shadow-2xl" data-testid="floating-rate">
        <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-neutral-500 mb-2">
          <span>Gram Altın · Canlı</span>
          <Radio size={10} className="animate-pulse" style={{color:"var(--gold)"}} />
        </div>
        <div className="font-display text-2xl tabular-nums" style={{color:"var(--gold)"}} data-testid="floating-rate-value">
          ₺{Number(goldGr).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className={`flex items-center gap-1 text-[10px] mt-1 ${goldChange >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          <TrendingUp size={10} />
          {goldChange >= 0 ? "+" : ""} ₺{Math.abs(goldChange).toFixed(2)} ({Math.abs(goldChangePct).toFixed(2)}%)
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const { t } = useI18n();

  // Authenticated users skip landing
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const features = [
    { icon: Radio, title: "Canlı Harem Altın Kurları", desc: "Harem Altın WebSocket bağlantısıyla milisaniye gecikmesiz gram altın, çeyrek, USD/TRY, gümüş ve platin fiyatları. Vitrin ekranınız Kapalıçarşı ile aynı anda dalgalanır — tezgahta kaybettiğiniz her saniye para." },
    { icon: Sparkles, title: "AI Ürün ve Ayar Tanıma", desc: "Vitrindeki bilezik, kolye, yüzük — Gemini 3.1 Pro ve Claude Sonnet 5 modelleri saniyeler içinde ayarı (14K/18K/22K/24K), gramaj tahminini ve kategoriyi otomatik çıkarır. Manuel veri girme devri kapanır, personel hatası düşer." },
    { icon: MessageCircle, title: "WhatsApp Dijital Fişi", desc: "Satış tamamlandığı anda müşterinin telefonuna profesyonel bir fiş: dükkan adı, ürün detayları, gram-ayar, işçilik kırılımı, anlık altın kuru ve QR karekod. Ek ücret yok, entegrasyon dakikalar içinde." },
    { icon: LineChart, title: "Gerçek Zamanlı Yönetim Paneli", desc: "Günlük hasılat, kar-zarar analizi, personel performansı, şube kırılımı, hurda-satış dengesi ve Z-Raporu — tümü canlı, tümü tek panelde. Vergi dönemleri için PDF ihracat, e-fatura entegrasyonuna hazır." },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background:"var(--bg)", color:"#e5e5e5"}} data-testid="landing-page">
      {/* Ambient gold radial */}
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: "radial-gradient(circle at 80% -10%, rgba(212,175,55,0.25), transparent 55%)",
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        background: "radial-gradient(circle at 10% 110%, rgba(212,175,55,0.18), transparent 45%)",
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)" }} data-testid="landing-nav">
        <Link to="/" className="block">
          <div className="font-display text-2xl sm:text-3xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-0.5">Precision Grade IV</div>
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          <LanguageSwitcher compact />
          <Link to="/login" className="text-sm text-neutral-300 hover:text-white transition-colors hidden sm:inline" data-testid="landing-login-link">Giriş Yap</Link>
          <Link to="/signup" className="text-sm px-4 py-2 border border-[--gold]/40 hover:bg-[--gold]/10 transition-colors" style={{color:"var(--gold)"}} data-testid="landing-nav-cta">
            Kuyumcunu Kaydet
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-8 sm:pt-12 lg:pt-16 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 space-y-8" data-testid="hero-content">
          <div className="inline-flex items-center gap-2 border border-[--gold]/30 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-mono" style={{color:"var(--gold)"}}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{background:"var(--gold)"}}></span>
            Türkiye'nin 1. Dijital Kuyumcu Platformu
          </div>
          <h1 className="font-display font-light text-3xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05]" data-testid="hero-headline">
            Kuyumculukta<br/>
            <span style={{color:"var(--gold)"}}>Dijital Devrim:</span><br/>
            Tezgahınızın her gramı<br/>
            <em className="not-italic" style={{color:"var(--gold)"}}>anlık kontrolünüzde.</em>
          </h1>
          <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-lg">
            Kapalıçarşı ustasının hızıyla, İstanbul Finans Merkezi'nin disiplini bir arada. Canlı Harem Altın kurları, AI ürün tanıma, WhatsApp dijital fişi, çok-şubeli POS ve profesyonel raporlama — kuyumcuya özel tek platformda.
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-2">
            <Link to="/signup" className="btn-primary-gold inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 text-sm sm:text-base" data-testid="hero-cta">
              Hemen Ücretsiz Deneyin <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white px-4 py-3.5" data-testid="hero-login">
              Zaten üyeyim <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="flex items-center gap-6 pt-4 text-[11px] font-mono tracking-wider text-neutral-500 border-t border-white/8 pt-6">
            <div className="flex items-center gap-2"><Lock size={12} style={{color:"var(--gold)"}} /> AES-256</div>
            <div className="flex items-center gap-2"><ShieldCheck size={12} style={{color:"var(--gold)"}} /> KVKK Uyumlu</div>
            <div className="flex items-center gap-2"><Zap size={12} style={{color:"var(--gold)"}} /> PayTR Güvencesi</div>
          </div>
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] font-mono tracking-widest uppercase"
            style={{
              borderColor: "rgba(212,175,55,0.28)",
              background: "linear-gradient(90deg, rgba(212,175,55,0.04), rgba(212,175,55,0.10), rgba(212,175,55,0.04))",
              color: "var(--gold)",
            }}
            data-testid="tpe-strip"
          >
            <span style={{ fontFamily: "serif" }}>MİLYEMSOFT®</span>
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-400">Türk Patent Enstitüsü nezdinde marka başvurusu kayıtlıdır</span>
          </div>
        </div>

        <div className="lg:col-span-6 relative" data-testid="hero-visual">
          <PosMockup />
        </div>
      </section>

      {/* Live ticker */}
      <DemoTicker />

      {/* Trust bar — 2.000+ kuyumcu/döviz bürosu */}
      <TrustBar />

      {/* Features */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28" data-testid="features-section">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="label-caps">Neden {COMPANY.brand}?</div>
          <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
            Kuyumcunun her ihtiyacı,<br/><span style={{color:"var(--gold)"}}>tek terminalde.</span>
          </h2>
          <p className="text-neutral-500 text-sm sm:text-base mt-5 leading-relaxed">
            Elektronik defter, e-fatura, hurda kabul, kasa ayrımı, personel yetkilendirme, KDV takibi ve fire yönetimi — hepsi tek panelde, tek kullanıcı deneyimiyle. Muhasebe ile POS'u ayrı yazılım kullanmaya son.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
          {features.map((f, i) => (
            <div key={i} className="group border border-white/8 bg-[#0a0a0a]/60 backdrop-blur p-7 sm:p-9 hover:border-[--gold]/30 transition-all duration-300 relative overflow-hidden" data-testid={`feature-card-${i}`}>
              <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{background:"radial-gradient(circle, rgba(212,175,55,0.15), transparent 70%)"}}></div>
              <div className="w-12 h-12 border border-[--gold]/40 flex items-center justify-center mb-5 group-hover:border-[--gold] transition-colors" style={{borderColor:"rgba(212,175,55,0.4)"}}>
                <f.icon size={20} style={{color:"var(--gold)"}} />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-light mb-3">{f.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 border-y border-white/8" style={{background:"#050505"}}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-14 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="stats-strip">
          {[
            { v: "0.2s", l: "Kur güncelleme" },
            { v: "5 Dil", l: "Türkçe · EN · AR · DE · RU" },
            { v: "17", l: "Profesyonel modül" },
            { v: "%99.9", l: "Uptime SLA" },
          ].map((s,i)=>(
            <div key={i} className="text-center sm:text-left">
              <div className="font-display text-3xl sm:text-4xl lg:text-5xl" style={{color:"var(--gold)"}}>{s.v}</div>
              <div className="label-caps mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32 text-center" data-testid="final-cta">
        <div className="label-caps">Kapalıçarşı'ndan Nişantaşı'na</div>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
          Dükkanınıza <span style={{color:"var(--gold)"}}>lüks bir imza</span> katın.
        </h2>
        <p className="text-neutral-400 mt-6 text-base sm:text-lg max-w-xl mx-auto">
          Kredi kartı gerektirmez · 30 gün ücretsiz tam erişim · Sözleşmesiz, istediğiniz an iptal.
        </p>
        <Link to="/signup" className="btn-primary-gold inline-flex items-center gap-2 mt-10 px-10 py-4 text-base" data-testid="final-cta-btn">
          Kuyumcunu Kaydet <ArrowRight size={16} />
        </Link>
        <div className="text-[11px] text-neutral-600 mt-6 font-mono tracking-wider">
          Standart Plan · 2.000 ₺ / ay · PayTR ile güvenli ödeme
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/8 mt-0" style={{background:"#040404"}}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-14 grid gap-10 md:grid-cols-12">
          {/* Brand + payment strip */}
          <div className="md:col-span-5">
            <div className="font-display text-2xl tracking-widest" style={{color:"var(--gold)"}}>{COMPANY.brand.toUpperCase()}</div>
            <p className="text-sm text-neutral-500 mt-3 max-w-sm leading-relaxed">
              Kapalıçarşı'dan ilham alan, kuyumcunun ihtiyacına göre tasarlanmış modern SaaS. 2026, İstanbul.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <div className="border border-white/10 px-3 py-2 text-[10px] font-mono tracking-widest text-neutral-400 flex items-center gap-2">
                <ShieldCheck size={12} style={{color:"var(--gold)"}} /> PayTR Güvencesiyle Ödeme
              </div>
              <div className="border border-white/10 px-3 py-2 text-[10px] font-mono tracking-widest text-neutral-400 flex items-center gap-2">
                <Lock size={12} style={{color:"var(--gold)"}} /> KVKK Uyumlu Veri Tabanı
              </div>
            </div>
            {/* Payment brand strip */}
            <div className="mt-6">
              <div className="label-caps mb-2.5">Kabul Edilen Ödeme Yöntemleri</div>
              <PaymentLogos size={30} />
            </div>
          </div>

          {/* Platform links */}
          <div className="md:col-span-2">
            <div className="label-caps mb-4">Platform</div>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li><Link to="/pricing" className="hover:text-[--gold]" style={{color:"var(--gold)"}}>Fiyatlandırma</Link></li>
              <li><Link to="/signup" className="hover:text-white">Ücretsiz Dene</Link></li>
              <li><Link to="/login" className="hover:text-white">Giriş Yap</Link></li>
            </ul>
          </div>

          {/* Legal links */}
          <div className="md:col-span-2">
            <div className="label-caps mb-4">Yasal</div>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li><Link to="/legal/mesafeli-satis" className="hover:text-white" data-testid="footer-link-mesafeli">Mesafeli Satış Sözleşmesi</Link></li>
              <li><Link to="/legal/iptal-iade" className="hover:text-white" data-testid="footer-link-iptaliade">İptal ve İade Koşulları</Link></li>
              <li><Link to="/legal/gizlilik-kvkk" className="hover:text-white" data-testid="footer-link-kvkk">Gizlilik / KVKK Politikası</Link></li>
              <li><Link to="/legal/hizmet-teslimati" className="hover:text-white" data-testid="footer-link-teslimat">Hizmet Teslimatı</Link></li>
              <li><Link to="/legal/kullanim-kosullari" className="hover:text-white" data-testid="footer-link-terms">Kullanım Koşulları</Link></li>
              <li><Link to="/iletisim" className="hover:text-white" data-testid="footer-link-iletisim">İletişim</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3" data-testid="footer-contact">
            <div className="label-caps mb-4">İletişim</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-neutral-300">
                <Phone size={14} className="mt-1 shrink-0" style={{color:"var(--gold)"}} />
                <a href={`tel:${COMPANY.phone.replace(/\s/g,"")}`} className="hover:text-white">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-2 text-neutral-300">
                <Mail size={14} className="mt-1 shrink-0" style={{color:"var(--gold)"}} />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all" style={{overflowWrap:"anywhere"}}>{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-2 text-neutral-500">
                <MapPin size={14} className="mt-1 shrink-0" style={{color:"var(--gold)"}} />
                <span>{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/6 py-5">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex flex-wrap items-center justify-between gap-3 text-[10px] font-mono tracking-widest text-neutral-600">
            <div data-testid="landing-copyright">© 2026 MilyemSoft - Mustafa Yıldız · Tüm Hakları Saklıdır.</div>
            <div>AES-256 · JWT · İzole Kasa · KVKK Uyumlu</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
